import axios from 'axios';
import { 
  InventoryItem, 
  InventoryFilter, 
  InventoryMetrics, 
  InventoryAlert,
  ReorderSuggestion,
  InventoryAnalytics,
  InventoryMovement
} from '../types';
import { authService } from '../../../services/authService';
import config from '../../../config';

const API_BASE_URL = `${config.api.baseUrl}/api`;

// Get auth headers from auth service
const getAuthHeaders = () => authService.getAuthHeaders();

class InventoryService {
  // Get inventory items with filters
  async getInventoryItems(filter?: InventoryFilter): Promise<{
    items: InventoryItem[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const response = await axios.post(
      `${API_BASE_URL}/inventory/items`,
      { filter },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get single inventory item by SKU
  async getInventoryItem(sku: string): Promise<InventoryItem> {
    const response = await axios.get(
      `${API_BASE_URL}/inventory/item/${sku}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get inventory metrics dashboard
  async getInventoryMetrics(): Promise<InventoryMetrics> {
    const response = await axios.get(
      `${API_BASE_URL}/inventory/metrics`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get inventory alerts
  async getInventoryAlerts(unresolved?: boolean): Promise<InventoryAlert[]> {
    const params = unresolved ? { unresolved: true } : {};
    const response = await axios.get(
      `${API_BASE_URL}/inventory/alerts`,
      { headers: getAuthHeaders(), params }
    );
    return response.data;
  }

  // Get reorder suggestions
  async getReorderSuggestions(): Promise<ReorderSuggestion[]> {
    const response = await axios.get(
      `${API_BASE_URL}/inventory/reorder-suggestions`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Update inventory item
  async updateInventoryItem(sku: string, updates: Partial<InventoryItem>): Promise<InventoryItem> {
    const response = await axios.put(
      `${API_BASE_URL}/inventory/item/${sku}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Adjust inventory quantity
  async adjustInventory(sku: string, adjustment: {
    quantity: number;
    reason: string;
    notes?: string;
  }): Promise<InventoryMovement> {
    const response = await axios.post(
      `${API_BASE_URL}/inventory/adjust/${sku}`,
      adjustment,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get inventory analytics
  async getInventoryAnalytics(params: {
    period: 'daily' | 'weekly' | 'monthly' | 'yearly';
    startDate: string;
    endDate: string;
  }): Promise<InventoryAnalytics> {
    const response = await axios.post(
      `${API_BASE_URL}/inventory/analytics`,
      params,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get inventory movements history
  async getInventoryMovements(sku?: string, limit: number = 100): Promise<InventoryMovement[]> {
    const params = sku ? { sku, limit } : { limit };
    const response = await axios.get(
      `${API_BASE_URL}/inventory/movements`,
      { headers: getAuthHeaders(), params }
    );
    return response.data;
  }

  // Export inventory data
  async exportInventory(format: 'csv' | 'excel'): Promise<Blob> {
    const response = await axios.get(
      `${API_BASE_URL}/inventory/export`,
      { 
        headers: getAuthHeaders(),
        params: { format },
        responseType: 'blob'
      }
    );
    return response.data;
  }

  // Bulk update inventory
  async bulkUpdateInventory(updates: Array<{
    sku: string;
    updates: Partial<InventoryItem>;
  }>): Promise<{
    success: number;
    failed: number;
    errors: Array<{ sku: string; error: string }>;
  }> {
    const response = await axios.post(
      `${API_BASE_URL}/inventory/bulk-update`,
      { updates },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Resolve inventory alert
  async resolveAlert(alertId: string, resolution?: string): Promise<InventoryAlert> {
    const response = await axios.post(
      `${API_BASE_URL}/inventory/alerts/${alertId}/resolve`,
      { resolution },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get low stock items
  async getLowStockItems(): Promise<InventoryItem[]> {
    const response = await axios.get(
      `${API_BASE_URL}/inventory/low-stock`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get expiring items (for beauty products)
  async getExpiringItems(daysAhead: number = 90): Promise<InventoryItem[]> {
    const response = await axios.get(
      `${API_BASE_URL}/inventory/expiring`,
      { 
        headers: getAuthHeaders(),
        params: { daysAhead }
      }
    );
    return response.data;
  }

  // Get dead/slow-moving inventory
  async getDeadInventory(daysSinceLastSale: number = 180): Promise<{
    items: InventoryItem[];
    totalValue: number;
    totalUnits: number;
  }> {
    const response = await axios.get(
      `${API_BASE_URL}/inventory/dead-stock`,
      { 
        headers: getAuthHeaders(),
        params: { daysSinceLastSale }
      }
    );
    return response.data;
  }

  // AI-powered demand forecasting
  async getDemandForecast(sku: string, days: number = 30): Promise<{
    sku: string;
    forecast: Array<{
      date: string;
      predictedDemand: number;
      confidence: number;
    }>;
    recommendedReorderPoint: number;
    recommendedOrderQuantity: number;
  }> {
    const response = await axios.get(
      `${API_BASE_URL}/inventory/forecast/${sku}`,
      { 
        headers: getAuthHeaders(),
        params: { days }
      }
    );
    return response.data;
  }
}

const inventoryService = new InventoryService();
export default inventoryService;
